<template>
	<div class="grid pr-40">
		<div class="mb-4">
			<div class="flex justify-between">
				<div class="flex w-full">
					<div class="flex flex-wrap">
						<BaseSelect
							ref="BaseInput.isRecordedByPolice"
							:required="true"
							:label="$t('recorded_by_police')"
							@input="updateValue"
							:options="policeRecordedOptions"
							v-model="isRecordedByPoliceOption"
							v-if="showRecordPolicy"
						/>
					</div>

					<div class="pt-8 ml-5" v-if="isRecordedByPoliceOption && isRecordedByPoliceOption.value && showRecordPolicy && showPoliceReportImage">
						<div class="flex w-full">
							<DocumentFileUploader :instant-upload="false" @input="uploadDocument">
								<BaseButton>
									<font-awesome-icon :icon="['fas', 'upload']" class="text-gray-700 mr-2" />
									{{ isAppContainer ? $t('take_photo_accident_document') : $t('upload_accident_document') }}
								</BaseButton>
							</DocumentFileUploader>
							<div class="text-red-700 px-8 py-2 font-bold" v-if="policeReportImage && isPdfTooLong">{{ $t('too_big_for_preview') }}</div>
						</div>
					</div>
				</div>
			</div>

			<div class="flex flex-wrap">
				<BaseDatepicker
					ref="BaseInput.date"
					class="w-full md:w-1/2 pr-0 md:pr-4"
					@input="updateValue"
					@select="updateValue"
					:required="true"
					:label="$t('accident_date')"
					v-model="date"
					:max_date="new Date()"
				/>
				<TimeInput v-if="showRecordPolicy" ref="BaseInput.time" @input="updateValue" class="w-full md:w-1/2" v-model="time" />
			</div>

			<div class="flex flex-wrap" v-if="showRecordPolicy">
				<BaseInput
					ref="BaseInput.accidentLocation"
					@input="updateValue"
					:required="true"
					class="w-full md:w-1/2 pr-0 md:pr-4"
					:label="$t('accident_location')"
					placeholder="Unfallort"
					mask="AXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
					type="text"
					v-model="damageCase.accidentLocation"
				/>
				<BaseSelect
					ref="BaseSelect"
					@input="updateValue"
					@change.native="updateValue"
					:label="$t('driver_passenger_injured')"
					:required="true"
					:options="injuredOptions"
					v-model="isPersonInjuredOption"
					v-if="showInjury"
				/>
			</div>
		</div>
		<div>
			<div v-if="isRecordedByPoliceOption && isRecordedByPoliceOption.value && showPoliceReportImage" class="h-full mb-24 mt-12">
				<div class="w-full h-full justify-start flex-grow">
					<div class="w-full h-full" style=" align-items: start" v-if="policeReportImage && !isPdfTooLong">
						<div v-if="!isDocument" class="grid grid-cols-2 w-full h-full justify-start" style="max-height: fit-content">
							<div class="flex flex-shrink justify-center w-full" style="align-items: center;">
								<BaseImage
									style="align-self: start; align-content: start"
									:src="policeReportImage"
									:alt="$t('accident_document')"
									:rotation="rotation"
									:show-rounded="false"
									@tempDoc="tempDoc = $event"
									class="flex justify-start h-3/4 w-full mr-4"
								/>
							</div>
							<div class="flex flex-grow w-full">
								<div class="mr-4">
									{{ $t('rotate_image_hint') }}
								</div>
								<div @click="rotation === 270 ? (rotation = 0) : (rotation += 90)" class="h-8 cursor-pointer">
									<font-awesome-icon :icon="['fas', 'sync-alt']" class="text-connectProBlue justify-start" size="lg" />
								</div>
							</div>
						</div>
						<PDFPreview width="100%" height="100%" :pdf-u-r-l="policeReportImage" v-else />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import BaseInput from '../../forms/inputs/BaseInput';
import BaseSelect from '@/components/forms/inputs/BaseSelect';
import { getTimeWithLeadingNull, getYesNoOption, getYesNoOptions } from '@/plugins/Utils';
import { stateMerge } from 'vue-object-merge';
import BaseDatepicker from '@/components/forms/inputs/BaseDatepicker';
import Authentication from '@/mixins/Authentication';
import PDFPreview from '@/components/damagecases/forms/PDFPreview';
import DocumentFileUploader from '@/components/forms/inputs/DocumentFileUploader';
import BaseButton from '@/components/forms/buttons/BaseButton';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import TimeInput from '@/components/forms/inputs/TimeInput';
import Validate from '@/mixins/Validate';
import BaseImage from '@/components/forms/BaseImage';

export default {
	components: {
		BaseImage,
		TimeInput,
		BaseButton,
		DocumentFileUploader,
		PDFPreview,
		BaseDatepicker,
		BaseSelect,
		BaseInput,
	},
	mixins: [Authentication, AppContainerMixin, Validate],
	props: {
		startValue: {},
		showInjury: { default: true },
		showRecordPolicy: { default: true },
		showPoliceReportImage: { default: true },
	},
	data() {
		return {
			damageCase: {},
			policeRecordedOptions: getYesNoOptions(),
			isRecordedByPoliceOption: null,
			date: null,
			time: null,
			injuredOptions: getYesNoOptions(),
			isPersonInjuredOption: null,
			isDamageTimeSetByUser: false,
			policeReportImage: null,
			isDocument: false,
			rotation: 0,
			tempDoc: null,
		};
	},
	computed: {
		isPdfTooLong() {
			// max. Anzahl Zeichen auf 10.000.000 gesetzt. Größere Dokumente verhindern das anzeigen der Vorschau.
			// 10.000.000 ist ein Testwert. Evtl. muss hier noch justiert werden.
			// Genauere Infos dazu konnte ich auf anhieb nicht finden, daher getestet und diesen Wert erstmal festgelegt
			return this.policeReportImage.length > 10000000;
		},
	},
	methods: {
		uploadDocument($event) {
			const type = $event.split('/');
			this.policeReportImage = $event;
			this.isDocument = type[0] !== 'data:image';

			const img = new Image();

			// eslint-disable-next-line @typescript-eslint/no-this-alias
			const context = this;
			img.onload = function() {
				if (img.width > img.height) {
					context.rotation === 270 ? (context.rotation = 0) : (context.rotation += 90);
				}
			};
			img.src = this.policeReportImage;

			this.updateValue();
		},
		getTimeAsUnixTimeStamp() {
			this.isDamageTimeSetByUser = this.time !== null && this.time !== '';
			const newTime = this.time || '00:00';

			if (this.showRecordPolicy) {
				if (this.date && this.$refs['BaseInput.time'].isValueValid(newTime)) {
					const time = newTime.split(':');
					this.date.setHours(parseInt(time[0]));
					this.date.setMinutes(parseInt(time[1]));
				}
			}

			const tmpDate = this.date || new Date();
			return parseInt(tmpDate.getTime() / 1000);
		},
		updateValue() {
			this.damageCase.isRecordedByPolice = this.isRecordedByPoliceOption ? this.isRecordedByPoliceOption.value : false;
			this.damageCase.date = this.date != null ? this.getTimeAsUnixTimeStamp() : null;
			this.damageCase.damageTimeSetByUser = this.isDamageTimeSetByUser != null ? this.isDamageTimeSetByUser : false;
			this.damageCase.isPersonInjured = this.isPersonInjuredOption != null ? this.isPersonInjuredOption.value : null;
			this.damageCase.policeReportImage = this.tempDoc;
			this.$emit('updateValue', this.damageCase);
		},
	},
	mounted() {
		if (this.startValue) {
			stateMerge(this.damageCase, this.startValue);

			this.date = this.damageCase.date ? new Date(this.damageCase.date * 1000) : null;

			this.isDamageTimeSetByUser = this.damageCase.damageTimeSetByUser;

			if (!this.time && this.damageCase.damageTimeSetByUser) {
				this.time = getTimeWithLeadingNull(this.date.getHours()) + ':' + getTimeWithLeadingNull(this.date.getMinutes());
			}

			this.isRecordedByPoliceOption = getYesNoOption(this.damageCase.isRecordedByPolice);
			this.isPersonInjuredOption = getYesNoOption(this.damageCase.isPersonInjured);
		}
	},
	watch: {
		startValue: function() {
			if (this.startValue) {
				stateMerge(this.damageCase, this.startValue);

				this.date = this.damageCase.date ? new Date(this.damageCase.date * 1000) : null;

				this.isDamageTimeSetByUser = this.damageCase.damageTimeSetByUser;

				if (!this.time && this.damageCase.damageTimeSetByUser) {
					this.time = getTimeWithLeadingNull(this.date.getHours()) + ':' + getTimeWithLeadingNull(this.date.getMinutes());
				}

				this.isRecordedByPoliceOption = getYesNoOption(this.damageCase.isRecordedByPolice);
				this.isPersonInjuredOption = getYesNoOption(this.damageCase.isPersonInjured);
			}
		},
	},
};
</script>

<style lang="sass" scoped>
.transition
  transition: transform 0.5s ease-in-out
</style>
